import Button from '@rio-cloud/rio-uikit/Button';
import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import MenuItem from '@rio-cloud/rio-uikit/MenuItem';
import { TableColumnsDropdown } from '@rio-cloud/rio-uikit/Table';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import TableToolbar, { TableToolbarColumn } from '@rio-cloud/rio-uikit/TableToolbar';
import TableViewToggles from '@rio-cloud/rio-uikit/TableViewToggles';
import { getTrackingAttributes } from '@rio-cloud/rio-uikit/googleAnalyticsUtils';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import {
    assetTableApplyFilterChanges,
    assetTableDisplayOptionsChanged,
    assetTableSearchChanged,
    assetTableViewTypeChanges,
    filterOptionsDialogClose,
    filterOptionsDialogOpen,
    getIsAssetsReloadImminent,
    getSearchString,
    getShowActiveAssets,
    getShowAssetsWithDataSource,
    getShowAssetsWithoutDataSource,
    getShowArchivedAssets,
    getShowFilterOptionsDialog,
    getTableCompactRows,
    getTableGridLines,
    openEasyOnboardingDialog,
} from '../../reducers/assets/assetsSlice';
import { CsvExport } from './CsvExport';
import type { Asset, TableColumnDetails } from './types';

interface Props {
    assets: Asset[];
    columns: TableColumnDetails[];
    showCreateButton?: boolean;
    handleColumnOrderChange: (columnOrder: string[]) => void;
    handleColumnVisibilityChange: (columnKey: string, visible: boolean) => void;
    handleColumnsReset: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;

const getToolbarCheckboxTrackingAttributes = (key: string) => ({
    ...getTrackingAttributes({
        trigger: 'click',
        category: 'checkbox autoTracking',
        action: 'checkbox click',
        label: key,
    }),
});

const filterButtonTrackingAttributes = {
    ...getTrackingAttributes({
        trigger: 'click',
        category: 'button autoTracking',
        action: 'button click',
        label: 'intl-msg:filter-options-dialog.toolbar',
    }),
};

export const AssetsListToolbar = (props: Props) => {
    const {
        assets,
        columns,
        showCreateButton = true,
        handleColumnOrderChange,
        handleColumnVisibilityChange,
        handleColumnsReset,
    } = props;

    const dispatch = useAppDispatch();
    const intl = useIntl();

    const searchString = useAppSelector(getSearchString);
    const showFilterOptionsDialog = useAppSelector(getShowFilterOptionsDialog);
    const showAssetsWithDataSource = useAppSelector(getShowAssetsWithDataSource);
    const showAssetsWithoutDataSource = useAppSelector(getShowAssetsWithoutDataSource);
    const showActiveAssets = useAppSelector(getShowActiveAssets);
    const showArchivedAssets = useAppSelector(getShowArchivedAssets);
    const gridLines = useAppSelector(getTableGridLines);
    const compactRows = useAppSelector(getTableCompactRows);
    const isAssetsReloadImminent = useAppSelector(getIsAssetsReloadImminent);

    const searchInputRef = useRef<HTMLInputElement>(null);
    const filterOptionsButtonRef = useRef<HTMLButtonElement>(null);
    const normalizedSearchString = searchString ?? '';
    const [localSearchString, setLocalSearchString] = useState(normalizedSearchString);

    useEffect(() => {
        searchInputRef.current?.focus();

        if (!filterOptionsButtonRef.current) {
            return;
        }

        for (const [key, value] of Object.entries(filterButtonTrackingAttributes)) {
            filterOptionsButtonRef.current.setAttribute(key, value);
        }
    }, []);

    useEffect(() => {
        setLocalSearchString(normalizedSearchString);
    }, [normalizedSearchString]);

    useEffect(() => {
        if (localSearchString === normalizedSearchString) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            dispatch(assetTableSearchChanged(localSearchString));
        }, SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [dispatch, localSearchString, normalizedSearchString]);

    const handleFilterChange = (nextFilterState: {
        showAssetsWithDataSource?: boolean;
        showAssetsWithoutDataSource?: boolean;
        showActiveAssets?: boolean;
        showArchivedAssets?: boolean;
    }) => {
        dispatch(
            assetTableApplyFilterChanges({
                showAssetsWithDataSource: nextFilterState.showAssetsWithDataSource ?? showAssetsWithDataSource,
                showAssetsWithoutDataSource: nextFilterState.showAssetsWithoutDataSource ?? showAssetsWithoutDataSource,
                showActiveAssets: nextFilterState.showActiveAssets ?? showActiveAssets,
                showArchivedAssets: nextFilterState.showArchivedAssets ?? showArchivedAssets,
            })
        );
    };

    const hasActiveExtraFilters =
        !showAssetsWithDataSource || !showAssetsWithoutDataSource || !showActiveAssets || !showArchivedAssets;
    const hasActiveTableOptions = gridLines || compactRows;
    const hasActiveDisplayOptions = hasActiveExtraFilters || hasActiveTableOptions;

    return (
        <TableToolbar>
            {showCreateButton && (
                <TableToolbarColumn>
                    <Button
                        bsStyle='primary'
                        iconName='plus'
                        autoTrackingKey='intl-msg:create.table-toolbar'
                        id='addAsset-new-table-toolbar'
                        data-cy='add-asset-new'
                        onClick={() => dispatch(openEasyOnboardingDialog())}
                    >
                        <FormattedMessage id='intl-msg:create' />
                    </Button>
                </TableToolbarColumn>
            )}
            {isAssetsReloadImminent && (
                <TableToolbarColumn>
                    <button type='button' className='btn btn-muted btn-loading btn-icon-only' />
                </TableToolbarColumn>
            )}
            {assets.length > 0 && (
                <TableToolbarColumn horizontalAlign='right' separator>
                    <CsvExport assets={assets} columns={columns} />
                </TableToolbarColumn>
            )}
            <TableToolbarColumn horizontalAlign='right'>
                <ButtonDropdown
                    ref={filterOptionsButtonRef}
                    title=''
                    aria-label={intl.formatMessage({
                        id: 'intl-msg:common-message.table.filter.options',
                        defaultMessage: 'Display options',
                    })}
                    iconName={hasActiveDisplayOptions ? 'filter-active' : 'filter'}
                    open={showFilterOptionsDialog}
                    onOpen={() => dispatch(filterOptionsDialogOpen())}
                    onClose={() => dispatch(filterOptionsDialogClose())}
                    pullRight
                    iconOnly
                    dropdownClassName='width-250 padding-5'
                    noCaret
                    data-cy='options-button'
                    customDropdown={
                        <ExtraFilterDropdownContent
                            showAssetsWithDataSource={showAssetsWithDataSource}
                            showAssetsWithoutDataSource={showAssetsWithoutDataSource}
                            showActiveAssets={showActiveAssets}
                            showArchivedAssets={showArchivedAssets}
                            gridLines={gridLines}
                            compactRows={compactRows}
                            onToggleShowAssetsWithDataSource={() =>
                                handleFilterChange({
                                    showAssetsWithDataSource: !showAssetsWithDataSource,
                                })
                            }
                            onToggleShowAssetsWithoutDataSource={() =>
                                handleFilterChange({
                                    showAssetsWithoutDataSource: !showAssetsWithoutDataSource,
                                })
                            }
                            onToggleShowActiveAssets={() =>
                                handleFilterChange({
                                    showActiveAssets: !showActiveAssets,
                                })
                            }
                            onToggleShowArchivedAssets={() =>
                                handleFilterChange({
                                    showArchivedAssets: !showArchivedAssets,
                                })
                            }
                            onToggleGridLines={() =>
                                dispatch(
                                    assetTableDisplayOptionsChanged({
                                        gridLines: !gridLines,
                                    })
                                )
                            }
                            onToggleCompactRows={() =>
                                dispatch(
                                    assetTableDisplayOptionsChanged({
                                        compactRows: !compactRows,
                                    })
                                )
                            }
                        />
                    }
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' separator className='min-width-200'>
                <FormattedMessage id={'intl-msg:search-placeholder'}>
                    {placeholder => (
                        <TableSearch
                            value={localSearchString}
                            inputRef={searchInputRef}
                            onChange={setLocalSearchString}
                            placeholder={placeholder}
                            data-cy='asset-table-search-input'
                        />
                    )}
                </FormattedMessage>
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right'>
                <TableViewToggles
                    onViewTypeChange={viewType => dispatch(assetTableViewTypeChanges(viewType))}
                    tableViewLabel={<FormattedMessage id='intl-msg:common-message.table.viewToggle.table' />}
                    singleCardViewLabel={<FormattedMessage id='intl-msg:common-message.table.viewToggle.list' />}
                    multiCardsViewLabel={<FormattedMessage id='intl-msg:common-message.table.viewToggle.cards' />}
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right'>
                <TableColumnsDropdown
                    columns={columns.map(column => ({
                        key: column.key,
                        label: intl.formatMessage({ id: column.label }),
                        visible: column.visible,
                    }))}
                    label={<FormattedMessage id='intl-msg:common-message.table.columnsDropdown.columns' />}
                    onColumnOrderChange={handleColumnOrderChange}
                    onColumnVisibilityChange={handleColumnVisibilityChange}
                    onReset={handleColumnsReset}
                    resetLabel={<FormattedMessage id='intl-msg:common-message.table.columnsDropdown.reset' />}
                    showResetButton={true}
                />
            </TableToolbarColumn>
        </TableToolbar>
    );
};

const ExtraFilterDropdownContent = ({
    showAssetsWithDataSource,
    showAssetsWithoutDataSource,
    showActiveAssets,
    showArchivedAssets,
    gridLines,
    compactRows,
    onToggleShowAssetsWithDataSource,
    onToggleShowAssetsWithoutDataSource,
    onToggleShowActiveAssets,
    onToggleShowArchivedAssets,
    onToggleGridLines,
    onToggleCompactRows,
}: {
    showAssetsWithDataSource: boolean;
    showAssetsWithoutDataSource: boolean;
    showActiveAssets: boolean;
    showArchivedAssets: boolean;
    gridLines: boolean;
    compactRows: boolean;
    onToggleShowAssetsWithDataSource: () => void;
    onToggleShowAssetsWithoutDataSource: () => void;
    onToggleShowActiveAssets: () => void;
    onToggleShowArchivedAssets: () => void;
    onToggleGridLines: () => void;
    onToggleCompactRows: () => void;
}) => (
    <>
        <MenuItem
            header
            value={
                <FormattedMessage id='intl-msg:common-message.table.filter.options' defaultMessage='Display options' />
            }
        />
        <div className='padding-x-10 padding-bottom-15'>
            <div className='space-y-20'>
                <div>
                    <div className='text-bold text-color-darkest margin-bottom-10'>
                        <FormattedMessage id='intl-msg:filter-options-dialog.data-sources' />
                    </div>
                    <div className='space-y-5'>
                        <Checkbox
                            className='text-wrap-balance'
                            checked={showAssetsWithDataSource}
                            label={<FormattedMessage id='intl-msg:filter-options-dialog.checkbox.with-data-source' />}
                            onChange={onToggleShowAssetsWithDataSource}
                            {...getToolbarCheckboxTrackingAttributes(
                                'intl-msg:filter-options-dialog.checkbox.with-data-source'
                            )}
                        />
                        <Checkbox
                            className='text-wrap-balance'
                            checked={showAssetsWithoutDataSource}
                            label={
                                <FormattedMessage id='intl-msg:filter-options-dialog.checkbox.without-data-source' />
                            }
                            onChange={onToggleShowAssetsWithoutDataSource}
                            {...getToolbarCheckboxTrackingAttributes(
                                'intl-msg:filter-options-dialog.checkbox.without-data-source'
                            )}
                        />
                    </div>
                </div>
                <div>
                    <div className='text-bold text-color-darkest margin-bottom-10'>
                        <FormattedMessage id='intl-msg:filter-options-dialog.asset-status' />
                    </div>
                    <div className='space-y-5'>
                        <Checkbox
                            checked={showActiveAssets}
                            label={<FormattedMessage id='intl-msg:filter-options-dialog.checkbox.show-active-assets' />}
                            onChange={onToggleShowActiveAssets}
                            {...getToolbarCheckboxTrackingAttributes(
                                'intl-msg:filter-options-dialog.checkbox.show-active-assets'
                            )}
                        />
                        <Checkbox
                            checked={showArchivedAssets}
                            label={
                                <FormattedMessage id='intl-msg:filter-options-dialog.checkbox.show-archived-assets' />
                            }
                            onChange={onToggleShowArchivedAssets}
                            {...getToolbarCheckboxTrackingAttributes(
                                'intl-msg:filter-options-dialog.checkbox.show-archived-assets'
                            )}
                        />
                    </div>
                </div>
                <div>
                    <div className='text-bold text-color-darkest margin-bottom-10'>
                        <FormattedMessage
                            id='intl-msg:common-message.table.displayOptions.table'
                            defaultMessage='Table'
                        />
                    </div>
                    <div className='space-y-5'>
                        <Checkbox
                            checked={gridLines}
                            label={
                                <FormattedMessage
                                    id='intl-msg:common-message.table.displayOptions.gridLines'
                                    defaultMessage='Grid lines'
                                />
                            }
                            onChange={onToggleGridLines}
                            {...getToolbarCheckboxTrackingAttributes(
                                'intl-msg:filter-options-dialog.checkbox.grid-lines'
                            )}
                        />
                        <Checkbox
                            checked={compactRows}
                            label={
                                <FormattedMessage
                                    id='intl-msg:common-message.table.displayOptions.condensed'
                                    defaultMessage='Compact rows'
                                />
                            }
                            onChange={onToggleCompactRows}
                            {...getToolbarCheckboxTrackingAttributes(
                                'intl-msg:filter-options-dialog.checkbox.compact-rows'
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    </>
);
